import React, { useState } from "react";
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useToast } from "@/components/ui/use-toast";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { PlusIcon } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ErrorType } from "@/types";
import { Membership } from "@/types/system-setting/membership";
import { DataTableRowActions } from "./data-table-row-actions";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import CreditForm from "../modal/form";
import {
  useGetMembershipsQuery,
  useUpdateMembershipsMutation,
} from "@/services/membershipsApi";
import { useGetIncomeCategorListQuery } from "@/services/incomeCategoryApi";
import { useGetSalesTaxListQuery } from "@/services/salesTaxApi";
import { useGetCategoriesQuery } from "@/services/categoryApi";
import { useGetPriceMastersQuery } from "@/services/priceMasterApi";
import TableFilters from "@/components/ui/table/data-table-filter";
import usePagination from "@/hooks/use-pagination";
import Pagination from "@/components/ui/table/pagination-table";
import { displayValue, formatToPKR, roundToTwoDecimals } from "@/lib/helper";
import { status } from "@/lib/constants/global";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import useTableData from "@/components/ui/table/data-table-logic";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import { MembershipStatus } from "@/types/enums";

const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
  type: "credits",
};

const durationLabels = {
  week: "Weeks",
  bi_annual: "Bi Annually",
  quarter: "Quaterly",
  month: "Months",
  year: "Years",
} as const;

interface SearchCriteriaType {
  limit: number;
  offset: number;
  sort_order: string;
  sort_key: string;
  search_key?: string;
  type: string;
}

export default function CreditsTableView() {
  const membership = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).membership ??
        "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;

  const { searchCriteria, query, toggleSortOrder, setSearchCriteria, inputValue, setInputValue } =
    useTableData(initialValue);

  const [openFilter, setOpenFilter] = useState(false);
  const [filterData, setFilterData] = useState<Record<string, any>>({});

  const {
    data: membershipsData,
    isLoading,
    refetch,
  } = useGetMembershipsQuery(
    { org_id: orgId, query: query+"&type=credit_add_ons" },
    {
      skip: query == "",
    }
  );
  const [updateMemberships] = useUpdateMembershipsMutation();

  const { data: groupData } = useGetCategoriesQuery({
    org_id: orgId,
    query: "type=credit_add_ons",
  });

  const { data: incomeCatData } = useGetIncomeCategorListQuery(orgId);

  const { data: priceMasterData } = useGetPriceMastersQuery({
    org_id: orgId,
    query: "item_type=credit_add_ons",
  });

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [action, setAction] = useState<"add" | "edit">("add");

  const membershipstableData = React.useMemo(() => {
    return Array.isArray(membershipsData?.data) ? membershipsData.data : [];
  }, [membershipsData]);

  const { toast } = useToast();

  const [data, setData] = useState<Membership | undefined>(undefined);
  const [sorting, setSorting] = useState<SortingState>([]);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});

  const handleStatusChange = async (payload: Membership) => {
    console.log({ payload });
    try {
      // payload.status=Boolean(payload.status)
      const resp = await updateMemberships(payload).unwrap();
      if (resp) {
        refetch();
        toast({
          variant: "success",
          title: "Credit Updated Successfully",
        });
      }
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `${typedError.data?.detail ?? (typedError.data as { message?: string }).message}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `Something Went Wrong.`,
        });
      }
    }
  };

  const handleEditMembership = (data: Membership) => {
    const updatedObject = {
      ...data,
      ...data.renewal_details,
    };
    setData(updatedObject);
    setAction("edit");
    setIsDialogOpen(true);
  };

  const actionsColumn: ColumnDef<Membership> = {
    id: "actions",
    header: "Actions",
    maxSize: 100,
    cell: ({ row }) => (
      <DataTableRowActions
        access={membership}
        data={row.original}
        refetch={refetch}
        handleEdit={handleEditMembership}
      />
    ),
  };

  const columns: ColumnDef<Membership>[] = [
    {
      accessorKey: "name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Credit Name"
          sortKey="name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="capitalize cursor-pointer text-nowrap">
                    <span>
                      {displayValue(
                        `${row.original.name}`.length > 20
                          ? `${row.original.name}`.substring(0, 20) + "..."
                          : `${row.original.name}`
                      )}
                    </span>
                  </p>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="capitalize text-sm">
                    {displayValue(`${row?.original?.name}`)}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "group_id",

      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Group"
          sortKey="group_id"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const group = groupData?.data?.find(
          (item) => item.id === row.original.group_id
        );
        return <span className="capitalize">{group?.name}</span>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "income_category_name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Income Category"
          sortKey="income_category_id"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <span className="text-nowrap">{`${displayValue(row.original.income_category_name)}`}</span>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "tax_group",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Tax Group"
          sortKey="tax_group"
          sortable={false}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <span className="text-nowrap">{row.original.tax_group && displayValue(row.original.tax_group.name)}</span>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "price_master_id",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Price"
          sortKey="price_master_id"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const rule = priceMasterData?.data?.find(
          (r) => r.id === row.original.price_master_id
        );
        return <span>{formatToPKR(rule?.price ?? 0)}</span>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "discount",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Discount"
          sortKey="discount"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const { discount } = row.original;

        return <span>{`${roundToTwoDecimals(discount as number)} %`}</span>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Status"
          sortKey="status"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const value =
          row.original?.status != null ? row.original?.status + "" : "false";
        const statusLabel = status.find((r) => r.value === value);

        return (
          <Select
            value={value}
            onValueChange={(e) =>
              handleStatusChange({ ...row.original, status: e as MembershipStatus, })
            }
            disabled={membership == "read"}
          >
            <SelectTrigger className="h-8">
              <SelectValue placeholder="Status" className="text-gray-400">
                <span className="flex gap-2 items-center">
                  <span
                    className={`${statusLabel?.color} rounded-[50%] w-4 h-4`}
                  ></span>
                  <span>{statusLabel?.label}</span>
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {status.map((item) => (
                <SelectItem key={item.value + ""} value={item.value + ""}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    ...(membership !== "read" ? [actionsColumn] : []),
  ];

  const table = useReactTable({
    data: membershipstableData as Membership[],
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
  });

  const handleOpen = () => {
    setAction("add");
    setIsDialogOpen(true);
    setData(undefined);
  };

  const handleFilterChange = (
    field: string,
    value: string | number | number[] | React.ChangeEvent<HTMLInputElement>
  ) => {
    let newValue;

    // Check if the value is coming from an input event or passed directly
    if (typeof value === "object" && "target" in value) {
      newValue = value.target.value;
    } else {
      newValue = value;
    }

    if (field === "discount_percentage") {
      newValue = Math.min(Number(newValue), 100); // Capping the discount percentage to 100
    }

    // Update the filter state based on field and value
    setFilterData((prev) => ({
      ...prev,
      [field]: newValue,
    }));
  };

  const filterDisplay = [
    {
      type: "select",
      name: "status",
      label: "Status",
      options: [
        { id: "active", name: "Active" },
        { id: "inactive", name: "Inactive" },
      ],
      function: (value: string) => handleFilterChange("status", value),
    },
    {
      type: "multiselect",
      name: "group_id",
      label: "Group",
      options: groupData?.data?.map((item) => ({
        value: item.id,
        label: item.name,
      })),
      function: (value: number) => handleFilterChange("group_id", value),
    },
    {
      type: "multiselect",
      name: "income_category_id",
      label: "Income Category",
      options: incomeCatData?.map((item) => ({
        value: item.id,
        label: item.name,
      })),
      function: (value: number) =>
        handleFilterChange("income_category_id", value),
    },
    {
      type: "percentage",
      name: "discount_percentage",
      label: "Discount Percentage",
      function: (event: React.ChangeEvent<HTMLInputElement>) =>
        handleFilterChange("discount_percentage", event),
    },
    {
      type: "number",
      name: "total_amount",
      label: "Total Amount",
      function: (event: React.ChangeEvent<HTMLInputElement>) =>
        handleFilterChange("total_amount", event),
    },
  ];

  const totalRecords = membershipsData?.filtered_counts ?? 0;
  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination<SearchCriteriaType>({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 px-3">
        {/* Table Filter component */}
        <TableSearchFilter setSearchCriteria={setSearchCriteria} inputValue={inputValue} setInputValue={setInputValue} />

        {/* Buttons Container */}
        <div className="flex flex-row lg:flex-row lg:justify-center lg:items-center gap-2">
          {membership !== "read" && (
            <Button
              className="bg-primary text-sm  text-black flex items-center gap-1  lg:mb-0 h-8 px-2"
              onClick={handleOpen}
            >
              <PlusIcon className="size-4" />
              Create New
            </Button>
          )}
          <button
            className={`border rounded-full size-3 text-gray-400 p-4 flex items-center justify-center ${Object.keys(filterData).length ? "border-primary" : ""}`}
            onClick={() => setOpenFilter(true)}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <i
                    className={`fa fa-filter ${Object.keys(filterData).length ? "text-primary" : ""}`}
                  ></i>
                </TooltipTrigger>
                <TooltipContent>
                  <p>click to apply filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>
        </div>
      </div>
      <div className="hidden md:block rounded-none border border-border  ">
        <ScrollArea className="w-full relative" hideScrollToTop={true}>
          <ScrollBar
            orientation="horizontal"
            className="relative z-30 cursor-grab h-2"
          />
          {/* <Table className="w-full overflow-x-scroll"> */}
          <Table className="w-full sm:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[60vh] 2xl:max-h-[65vh]  ">
            {" "}
            {/* //overflow-y-auto scrollbar-thin */}
            <TableHeader className="bg-secondary">
              {table?.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    <div className="flex space-x-2 justify-center items-center bg-white ">
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    onClick={(e) => {
                      if (membership !== "read" && (e.target instanceof Element &&
                        !e.target.closest('[role="checkbox"]') && (e.target === e.currentTarget || e.target.closest('td')))) {
                        handleEditMembership(row.original)
                      }
                    }}
                    className={membership !== "read" ? "cursor-pointer hover:bg-secondary" : ""}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : membershipstableData.length > 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No credit found.
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No records found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {/* Mobile Card View */}
      <div className="block md:hidden space-y-3 px-3">
        {isLoading ? (
          <div className="flex space-x-2 justify-center items-center py-10">
            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
            <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
            <div className="size-3 bg-black rounded-full animate-bounce"></div>
          </div>
        ) : membershipstableData.length ? (
          membershipstableData.map((row: Membership) => {
            const group = groupData?.data?.find((item) => item.id === row.group_id);
            const rule = priceMasterData?.data?.find((r) => r.id === row.price_master_id);
            return (
              <div
                key={row.id}
                className="border border-border rounded-md p-3 space-y-2 bg-white"
                onClick={() => {
                  if (membership !== "read") {
                    handleEditMembership(row);
                  }
                }}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-sm capitalize truncate">
                      {displayValue(row.name)}
                    </p>
                    <p className="text-xs text-gray-500 capitalize truncate">
                      {group?.name || "—"}
                    </p>
                  </div>
                  <div
                    className="shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {membership !== "read" && (
                      <DataTableRowActions
                        access={membership}
                        data={row}
                        refetch={refetch}
                        handleEdit={handleEditMembership}
                      />
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-y-1 text-xs">
                  <div className="text-gray-500">Income Category</div>
                  <div className="text-right">{displayValue(row.income_category_name)}</div>

                  <div className="text-gray-500">Tax Group</div>
                  <div className="text-right">{row.tax_group && displayValue(row.tax_group.name)}</div>

                  <div className="text-gray-500">Price</div>
                  <div className="text-right">{formatToPKR(rule?.price ?? 0)}</div>

                  <div className="text-gray-500">Discount</div>
                  <div className="text-right">{roundToTwoDecimals(row.discount as number)}%</div>

                  <div className="text-gray-500">Status</div>
                  <div className="text-right capitalize">
                    {status.find((r) => r.value === (row.status != null ? row.status + "" : "false"))?.label}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="h-24 flex items-center justify-center text-sm text-gray-500">
            No records found.
          </div>
        )}
      </div>

      {/* pagination */}
      {
        membershipstableData.length > 0 && (
          <Pagination
            limit={searchCriteria.limit}
            offset={searchCriteria.offset}
            totalItems={totalRecords}
            onLimitChange={handleLimitChange}
            onNextPage={handleNextPage}
            onPrevPage={handlePrevPage}
            onFirstPage={handleFirstPage}
            onLastPage={handleLastPage}
            isLastPage={isLastPage}
          />
        )
      }

      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={setSearchCriteria}
        filterDisplay={filterDisplay}
        setInputValue={setInputValue}
      />

      <CreditForm
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
        data={data}
        setData={setData}
        refetch={refetch}
        action={action}
        setAction={setAction}
      />
    </div >
  );
}
